import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { api } from '../api/client';
import { useAuthStore } from '../stores/authStore';
import { usePageTitle } from '../hooks/usePageTitle';

interface Profile {
  id: string;
  email: string;
  name: string;
  role: string;
  phone: string | null;
  location: string | null;
  profile_type: string | null;
  linkedin_url: string | null;
  created_at: string;
}

export function Settings() {
  usePageTitle('Settings');
  const { user, setAuth, accessToken, refreshToken } = useAuthStore();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  // Profile form
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [location, setLocation] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');

  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMsg, setPasswordMsg] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Avatar
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get<Profile>('/auth/me')
      .then((p) => {
        setProfile(p);
        setName(p.name);
        setPhone(p.phone || '');
        setLocation(p.location || '');
        setLinkedinUrl(p.linkedin_url || '');
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSavedMsg('');
    try {
      await api.patch('/auth/profile', { name, phone, location, linkedin_url: linkedinUrl });
      // Update the auth store with new name
      if (user && accessToken && refreshToken) {
        setAuth({ ...user, name }, accessToken, refreshToken);
      }
      setSavedMsg('Profile updated');
      setTimeout(() => setSavedMsg(''), 3000);
    } catch (err) {
      setSavedMsg('Failed to update');
    }
    setSaving(false);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordMsg('');

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    setChangingPassword(true);
    try {
      await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setPasswordMsg('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordMsg(''), 3000);
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Failed to change password');
    }
    setChangingPassword(false);
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      await api.post('/auth/avatar', formData);
    } catch (err) {
      console.error('Upload failed:', err);
    }
    setUploading(false);
  };

  if (loading) return <Layout><div className="max-w-2xl mx-auto mt-20 px-6"><p className="text-mustard-charcoal/60">Loading...</p></div></Layout>;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto mt-10 px-6 pb-20">
        <h1 className="font-display text-3xl font-bold text-mustard-green-dark mb-8">Settings</h1>

        {/* Avatar */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6">
          <h2 className="font-display text-lg font-bold text-mustard-green-dark mb-4">Profile Picture</h2>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-mustard-green-dark text-white flex items-center justify-center text-2xl font-bold">
              {name.charAt(0).toUpperCase()}
            </div>
            <div>
              <label className="inline-block px-4 py-2 bg-mustard-green-dark text-white text-sm rounded-lg font-medium cursor-pointer hover:bg-mustard-green transition-colors">
                {uploading ? 'Uploading...' : 'Upload Photo'}
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleAvatarUpload} className="hidden" disabled={uploading} />
              </label>
              <p className="text-xs text-mustard-charcoal/40 mt-1">JPG, PNG or WebP. Max 2MB.</p>
            </div>
          </div>
        </div>

        {/* Profile Form */}
        <form onSubmit={handleSaveProfile} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6">
          <h2 className="font-display text-lg font-bold text-mustard-green-dark mb-4">Profile Details</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-mustard-charcoal mb-1">Full Name</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-mustard-green focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-mustard-charcoal mb-1">Email</label>
              <input type="email" value={profile?.email || ''} disabled className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-mustard-charcoal/50" />
              <p className="text-xs text-mustard-charcoal/40 mt-1">Email cannot be changed</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-mustard-charcoal mb-1">Phone</label>
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-mustard-green focus:border-transparent outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-mustard-charcoal mb-1">Location</label>
                <input type="text" value={location} onChange={(e) => setLocation(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-mustard-green focus:border-transparent outline-none" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-mustard-charcoal mb-1">LinkedIn URL</label>
              <input type="url" value={linkedinUrl} onChange={(e) => setLinkedinUrl(e.target.value)} placeholder="https://linkedin.com/in/..." className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-mustard-green focus:border-transparent outline-none" />
            </div>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <button type="submit" disabled={saving} className="px-6 py-2 bg-mustard-green-dark text-white rounded-lg text-sm font-medium disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            {savedMsg && <span className="text-sm text-green-600">{savedMsg}</span>}
          </div>
        </form>

        {/* Password Change */}
        <form onSubmit={handleChangePassword} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h2 className="font-display text-lg font-bold text-mustard-green-dark mb-4">Change Password</h2>
          {passwordError && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded mb-4 text-sm">{passwordError}</div>}
          {passwordMsg && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-2 rounded mb-4 text-sm">{passwordMsg}</div>}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-mustard-charcoal mb-1">Current Password</label>
              <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-mustard-green focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-mustard-charcoal mb-1">New Password</label>
              <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={8} className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-mustard-green focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-mustard-charcoal mb-1">Confirm New Password</label>
              <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-mustard-green focus:border-transparent outline-none" />
            </div>
          </div>
          <button type="submit" disabled={changingPassword} className="mt-4 px-6 py-2 bg-mustard-green-dark text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {changingPassword ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>
    </Layout>
  );
}
